<template>
  <BlockContainer class="p-5">
    <ValidationObserver v-slot="{ dirty }">
      <div class="row align-items-center">
        <div class="col col-lg-8">
          <h5>Leieobjektets husordensregler</h5>
        </div>

        <div class="col edit-buttons text-right">
          <div v-if="editMode">
            <CancelButton class="mr-1" @click.native="editMode = false" />
            <SaveButton
              :loading="loading"
              @click.native="patchRentalObject"
              :disabled="!dirty || loading"
              class="ml-1"
            />
          </div>

          <EditButton v-else @click.native="setEditModeAndLocalCopy" />
        </div>
      </div>

      <div class="row mt-5">
        <div class="col">
          <h6>Dyrehold</h6>

          <!-- Loading Mask -->
          <div v-if="!loaded" class="mt-4 pt-3">
            <hl-Mask></hl-Mask>
            <hl-Mask :width="80" class="ml-3"></hl-Mask>
          </div>
          <!-- / Loading Mask -->

          <div v-else>
            <ValidationProvider v-if="editMode">
              <hl-Switch
                class="d-inline-block mt-4 mb-5"
                v-model="localCopy.animalsAllowed"
                showState
                onState="Tillatt"
                offState="Ikke tillatt"
              ></hl-Switch>
            </ValidationProvider>

            <div v-else class="mt-4 pt-3">
              <i v-if="details.animalsAllowed" class="budicon-check-ui"></i>
              <i v-else class="budicon-block-sign"></i>
              <span class="ml-3">{{ details.animalsAllowed ? 'Tillatt' : 'Ikke tillatt' }}</span>
            </div>
          </div>
        </div>

        <div class="col">
          <h6>Røyking</h6>

          <!-- Loading Mask -->
          <div v-if="!loaded" class="mt-4 pt-3">
            <hl-Mask></hl-Mask>
            <hl-Mask :width="80" class="ml-3"></hl-Mask>
          </div>
          <!-- / Loading Mask -->

          <div v-else>
            <ValidationProvider v-if="editMode">
              <hl-Switch
                class="d-inline-block mt-4 mb-5"
                v-model="localCopy.smokingAllowed"
                showState
                onState="Tillatt"
                offState="Ikke tillatt"
              ></hl-Switch>
            </ValidationProvider>

            <div v-else class="mt-4 pt-3">
              <i v-if="details.smokingAllowed" class="budicon-check-ui"></i>
              <i v-else class="budicon-block-sign"></i>
              <span class="ml-3">{{ details.smokingAllowed ? 'Tillatt' : 'Ikke tillatt' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-5">
        <div>
          <h6>Husordensregler</h6>

          <!-- Loading Mask -->
          <div v-if="!loaded">
            <hl-Mask :height="60" width="100%"></hl-Mask>
          </div>
          <!-- / Loading Mask -->

          <div v-else>
            <ValidationProvider v-if="editMode">
              <hl-Textarea v-model="localCopy.houseRules" :rows="6"></hl-Textarea>
            </ValidationProvider>
            <div v-else>
              <pre class="mt-4 pre-scrollable pre-responsive">{{ details.houseRules ? details.houseRules : '' }}</pre>
              <div v-if="details.houseRules.length > 1024" class="scroll-down text-center">
                <span>Scroll down</span>
                <i class="budicon-arrow-bottom ml-3"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4">
          <h6>Særlige bestemmelser</h6>

          <!-- Loading Mask -->
          <div v-if="!loaded">
            <hl-Mask :height="60" width="100%"></hl-Mask>
          </div>
          <!-- / Loading Mask -->

          <div v-else>
            <ValidationProvider v-if="editMode">
              <hl-Textarea v-model="localCopy.specialRemarks" :rows="6"></hl-Textarea>
            </ValidationProvider>
            <div v-else>
              <pre class="mt-4 pre-scrollable pre-responsive">{{
                details.specialRemarks ? details.specialRemarks : ''
              }}</pre>
              <div v-if="details.specialRemarks.length > 1024" class="scroll-down text-center">
                <span>Scroll down</span>
                <i class="budicon-arrow-bottom ml-3"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </BlockContainer>
</template>

<script>
import BlockContainer from '@/components/BlockContainer.vue';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import { mapGetters } from 'vuex';
import Mask from '@modules/husleie-framework/src/components/loaders/Mask.vue';
import Switch from '@modules/husleie-framework/src/components/forms/inputs/switch/Switch.vue';
import Textarea from '@modules/husleie-framework/src/components/forms/Textarea.vue';

export default {
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    SaveButton,
    'hl-Mask': Mask,
    'hl-Switch': Switch,
    'hl-Textarea': Textarea,
  },

  props: {
    rentalObjectId: [Number, String],
  },

  data() {
    return {
      editMode: false,
      localCopy: null,
      loading: false,
    };
  },

  computed: {
    ...mapGetters(['getRentalObject', 'isRentalObjectPatching', 'isExtendedRentalObjectLoaded']),

    loaded() {
      return this.isExtendedRentalObjectLoaded;
    },

    details() {
      return this.getRentalObject(this.rentalObjectId).details;
    },

    patching() {
      return this.isRentalObjectPatching;
    },

    patchBody() {
      if (this.localCopy == null) return;
      return {
        details: {
          smokingAllowed: this.localCopy.smokingAllowed,
          animalsAllowed: this.localCopy.animalsAllowed,
          houseRules: this.localCopy.houseRules,
          specialRemarks: this.localCopy.specialRemarks,
        },
      };
    },
  },

  methods: {
    setEditModeAndLocalCopy() {
      this.editMode = true;
      this.$set(this, 'localCopy', JSON.parse(JSON.stringify(this.details)));
    },

    patchRentalObject() {
      this.loading = true;
      this.$store.dispatch('patchRentalObject', {
        rentalObjectId: this.rentalObjectId,
        body: this.patchBody,
        success: () => {
          this.editMode = false;
          this.$toast('Lagret', 'success');
        },
        error: () => {
          this.showErrorModal = true;
          this.$toast('Kunne ikke lagre', 'error');
        },
      });
      this.loading = false;
    },
  },
};
</script>
